import {AfterViewChecked, ChangeDetectorRef, Component, Injector, OnInit, ViewChild} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {GenericListDialogComponent} from '../../../../common/dialogs/generic-list-dialog/generic-list-dialog.component';
import {AddAtcDialogComponent} from '../../dialogs/add-atc-dialog/add-atc-dialog.component';
import {NameValueDTO} from 'src/app/modules/common/models/NameValueDTO';
import {HelpDTO} from 'src/app/modules/other/models/HelpDTO';
import {IngredientDTO} from 'src/app/modules/pbm/models/IngrdientDTO';
import {MatStepper} from '@angular/material/stepper';
import {HelpService} from 'src/app/modules/other/services/help.service';
import {LookupService} from 'src/app/modules/common/services/lookup.service';
import {KeycloakService} from 'keycloak-angular';
import {IngredientService} from 'src/app/modules/pbm/services/ingredient.service';
import {throwError} from 'rxjs';
import {DisplayIcon} from '../../../../common/components';
import {SharedService} from '../../../../common/services/shared-service';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';

@Component({
  selector: 'app-add-ingredient-view',
  templateUrl: './add-ingredient-view.component.html',
  styleUrls: [
    '../../../../../../styles/global/_add-view-edit-styles.scss'
  ]
})
export class AddIngredientViewComponent implements OnInit, AfterViewChecked {
  readonly DisplayIcon = DisplayIcon;
  readonly lookupValue = LookupValue;

  ingredientNameFormControl: FormControl;
  pubchemFormControl: FormControl;
  pharmacologicalClassificationFormControl: FormControl;
  fdaPharmacologyFormControl: FormControl;
  meshFormControl: FormControl;
  sourceFormControl: FormControl;
  atcCodeFormControl: FormControl;
  detailFormGroup: FormGroup;
  atcFormGroup: FormGroup;
  allAtcCodes: NameValueDTO[];
  filteredAtcCodes: NameValueDTO[] = [];
  helpInfo: HelpDTO[];
  userFirstLastName = '';
  isDialog = false;
  dialogRef = null;
  dialogData;
  editIngredient = new IngredientDTO();
  isEdit = false;

  @ViewChild('stepper') private ingredientStepper: MatStepper;

  constructor(
    private changeDetect: ChangeDetectorRef,
    private injector: Injector,
    private formBuilder: FormBuilder,
    private sharedService: SharedService,
    private router: Router,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private keycloakService: KeycloakService,
    private lookupService: LookupService,
    private helpService: HelpService,
    private ingredientService: IngredientService
  ) {
    this.dialogRef = this.injector.get(MatDialogRef, null);
    this.dialogData = this.injector.get(MAT_DIALOG_DATA, null);

    if (this.dialogData != null) {
      this.isDialog = this.dialogData;
    }

    this.ingredientNameFormControl = new FormControl('');
    this.pubchemFormControl = new FormControl('');
    this.pharmacologicalClassificationFormControl = new FormControl('');
    this.fdaPharmacologyFormControl = new FormControl('');
    this.meshFormControl = new FormControl('');
    this.sourceFormControl = new FormControl('');
    this.atcCodeFormControl = new FormControl('');

    this.detailFormGroup = this.formBuilder.group({
      ingredientName: this.atcCodeFormControl,
      pubchem: this.pubchemFormControl,
      pharmacologicalClassification: this
        .pharmacologicalClassificationFormControl,
      fdaPharmacology: this.fdaPharmacologyFormControl,
      mesh: this.meshFormControl,
      source: this.sourceFormControl
    });

    this.atcFormGroup = this.formBuilder.group({
      atcCode: this.atcCodeFormControl,
      atcCodes: this.formBuilder.array([], [Validators.minLength(2)])
    });
  }

  get atcCodes() {
    return this.atcFormGroup.get('atcCodes') as FormArray;
  }

  ngOnInit(): void {
    if (!this.isDialog) {
      if (this.route.snapshot.url[1].path === 'edit-ingredient') {
        this.isEdit = true;
        this.route.paramMap.subscribe((params) => {
          this.getIngredient(Number(params.get('id')));
        });
      }
    }

    this.getListOfLookupValues(this.lookupValue.Atc);

    this.getHelpInfo('ingredient');

    this.keycloakService.loadUserProfile().then((profile) => {
      this.userFirstLastName = `${profile.firstName} ${profile.lastName}`;
    });
  }

  ngAfterViewChecked(): void {
    this.changeDetect.detectChanges();
  }

  getIngredient(ingredientId: number) {
    this.ingredientService.getIngredient(ingredientId).subscribe(
      (response) => {
        this.editIngredient = response;
        this.populateForms();
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openErrorDialog(error.businessMessage);
      }
    );
  }

  populateForms() {
    this.ingredientNameFormControl = new FormControl(this.editIngredient.name);
    this.pubchemFormControl = new FormControl(this.editIngredient.pubchemName);
    this.pharmacologicalClassificationFormControl = new FormControl(this.editIngredient.pharmacologicalClassification);
    this.fdaPharmacologyFormControl = new FormControl(this.editIngredient.fdaPharmacology);
    this.meshFormControl = new FormControl(this.editIngredient.mesh);
    this.sourceFormControl = new FormControl(this.editIngredient.source);
    this.editIngredient.atcCodes.forEach((atc) => {
      this.atcCodes.push(new FormControl(atc.name));
    });
  }

  getListOfLookupValues(entity: string): void {
    this.lookupService.getListOfLookupValues(entity).subscribe(
      (response: NameValueDTO[]) => {
        if (entity === this.lookupValue.Atc) {
          this.allAtcCodes = response;
        }
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openErrorDialog(error.businessMessage);
      }
    );
  }

  getHelpInfo(componentName: string) {
    this.helpService.getListOfHelpInfo(componentName).subscribe(
      (response) => {
        this.helpInfo = response;
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openErrorDialog(error.businessMessage);
      }
    );
  }

  filterAtcCodes() {
    const filterValue = this.atcCodeFormControl.value.toLowerCase();

    if (filterValue.length > 0) {
      this.filteredAtcCodes = this.allAtcCodes
        .filter((option) => option.name.toLowerCase().indexOf(filterValue) >= 0)
        .slice(0, 99);
    }
  }

  addAtcCode() {
    if (
      this.filteredAtcCodes.find(
        (item) => item.name === this.atcCodeFormControl.value
      ) != null
    ) {
      const atcCode = new FormControl(this.atcCodeFormControl.value);
      this.atcCodes.push(atcCode);
    }

    this.atcCodeFormControl.setValue('');
    this.filterAtcCodes();
  }

  removeAtcCode(index: number) {
    this.atcCodes.removeAt(index);
  }

  finish() {
    const ingredientDTO = new IngredientDTO();
    try {
      ingredientDTO.name = this.ingredientNameFormControl.value;
      ingredientDTO.fdaPharmacology = this.fdaPharmacologyFormControl.value;
      ingredientDTO.mesh = this.meshFormControl.value;
      ingredientDTO.pharmacologicalClassification = this.pharmacologicalClassificationFormControl.value;
      ingredientDTO.pubchemName = this.pubchemFormControl.value;
      ingredientDTO.source = this.sourceFormControl.value;

      const newAtcCodes: NameValueDTO[] = [];
      this.atcCodes.controls.forEach((control) => {
        newAtcCodes.push({
          value: this.allAtcCodes.find((item) => item.name === control.value)
            .value,
          name: control.value
        });
      });
      ingredientDTO.atcCodes = newAtcCodes;

      if (this.isEdit) {
        ingredientDTO.ingredientId = this.editIngredient.ingredientId;
        ingredientDTO.updatedBy = this.userFirstLastName;
        ingredientDTO.created = this.editIngredient.created;
        ingredientDTO.createdBy = this.editIngredient.createdBy;
      }
      else {
        ingredientDTO.created = new Date();
        ingredientDTO.createdBy = this.userFirstLastName;
      }

      this.ingredientService.saveIngredient(ingredientDTO).subscribe(
        (response) => {
          this.sharedService.openSnackBar(
            `Ingredient ${this.ingredientNameFormControl.value} saved successfully`,
            'Dismiss'
          );
          if (this.isDialog) {
            this.dialogRef.close();
          }
          else {
            this.router.navigate(['/pharmacy-management'], {queryParams: {tab: 'ingredients'}});
          }
        },
        (error) => {
          throwError(error);
        }
      );
    } catch (e) {
      this.sharedService.openErrorDialog((e as Error).message);
      this.ingredientStepper.selectedIndex = 0;
    }
  }

  openGenericDialog(displayHeading: string): void {
    const displayList: string[] = [];

    if (displayHeading === 'ATC Code(s)') {
      const atcCodeList = this.atcCodes.controls;
      for (let i = 0; i < atcCodeList.length; i++) {
        const displayString = atcCodeList[i].value;
        displayList.push(displayString);
      }
    }

    const dialogRef = this.dialog.open(GenericListDialogComponent, {
      width: '40vw',
      data: {list: displayList, heading: displayHeading}
    });
  }

  openAtcDialog() {
    const dialogRef = this.dialog.open(AddAtcDialogComponent, {
      width: '40vw'
    });
  }

  getInfoTooltip(inputName: string): string {
    let message = '';
    if (this.helpInfo != null) {
      const helpInfo = this.helpInfo.find((help) => help.inputName === inputName);
      message = helpInfo != null ? helpInfo.inputInfo : '';
    }
    return message;
  }
}
